<template>
	<v-ons-page>
		<div class="pain-note-report">
			<!-- ローディング -->
			<transition name="fade">
				<webview-redraw-progress v-if="isLoading" />
			</transition>
			<!-- /ローディング -->
			<!-- エラー処理 -->
			<transition name="fade">
				<sorry-page v-show="isError" @reload="reload" />
			</transition>
			<!-- /エラー処理 -->
			<div class="pain-note-report__inner">
				<div class="pain-note-report-monthbar">
					<v-ons-icon class="pain-note-report-monthbar__icon" icon="md-chevron-left" @click="exchangeMonth(-1)" />
					{{ computedMonthBarDate }}
					<v-ons-icon class="pain-note-report-monthbar__icon" icon="md-chevron-right" :class="{'is-disabled': isMonthDisabled}" @click="exchangeMonth(1)" />
				</div>
			</div>
			<div class="pain-note-report__content">
				<!-- 人：痛み回数、ペット：不調の回数 -->
				<div class="pain-note-report-pain">
					<h2>{{ petType !== 1 ? '不調の回数' : '痛み回数' }}</h2>
					<p>{{ computedPainCount }}</p>
					<div class="pain-note-report-pain__area">
						<div class="pain-note-report-pain__area--left">
							<i :class="`pain-report_sprite${petClass} sprite-inp_icon_pain2_4`" />
							<i :class="`pain-report_sprite${petClass} sprite-inp_icon_pain3_4`" />
							<i :class="`pain-report_sprite${petClass} sprite-inp_icon_pain4_4`" />
						</div>
						<vue-c3 :handler="painLevel" class="pain-note-report-pain__area--right" />
					</div>
					<vue-c3 :handler="painHour" class="pain-note-report-pain__hour" />
				</div>
				<!-- 人：クスリ回数、ペット：非表示 -->
				<div v-if="petType === 1" class="pain-note-report-drug">
					<h2>クスリ回数</h2>
					<p>{{ computedDrugCount }}</p>
					<div class="pain-note-report-drug__area">
						<div class="pain-note-report-drug__area--left">
							<i class="pain-report_sprite sprite-inp_icon_drug_otc_3" />
							<i class="pain-report_sprite sprite-inp_icon_drug_pre_3" />
						</div>
						<vue-c3 :handler="drugLevel" class="pain-note-report-drug__area--right" />
					</div>
					<vue-c3 :handler="drugHour" class="pain-note-report-drug__hour" />
				</div>
				<!-- 人：痛み発生時の気圧、ペット：痛み発生時の気圧 -->
				<div class="pain-note-report-barometricpressure">
					<h2>{{ petType !== 1 ? '不調発生時の気圧' : '痛み発生時の気圧' }}</h2>
					<div class="pain-note-report-barometricpressure__area">
						<vue-c3 :handler="barometricPressure" class="pain-note-report-barometricpressure__area--pie" />
						<div class="pain-note-report-barometricpressure__area--text" v-if="!barometricPressureDataCheck">
							<p>データがありません</p>
						</div>
					</div>
					<div class="pain-note-report-barometricpressure__label">
						<div class="pain-note-report-barometricpressure__label--left">
							<p>気圧降下時</p>
						</div>
						<div class="pain-note-report-barometricpressure__label--right">
							<p>気圧上昇時</p>
						</div>
					</div>
				</div>
				<!-- 人：痛み発生時の天気、ペット：不調発生時の天気 -->
				<div class="pain-note-report-weather">
					<h2>{{ petType !== 1 ? '不調発生時の天気' : '痛み発生時の天気' }}</h2>
					<div class="pain-note-report-weather__area">
						<vue-c3 :handler="weather" class="pain-note-report-weather__area--pie" />
						<div class="pain-note-report-weather__area--text" v-if="!weatherDataCheck">
							<p>データがありません</p>
						</div>
					</div>
					<div class="pain-note-report-weather__label">
						<div class="pain-note-report-weather__label--left">
							<p>晴れ</p>
						</div>
						<div class="pain-note-report-weather__label--center">
							<p>曇り</p>
						</div>
						<div class="pain-note-report-weather__label--right">
							<p>雨/雪</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-ons-page>
</template>

<script>
// Vuex
import { mapActions, mapGetters } from 'vuex'
import { SET_REPORT_DATA } from '../store/modules/painNote/mutation-types'

// Compornents
import SorryPage from '@/views/SorryPage'
import WebviewRedrawProgress from '@/components/Molecules/WebviewRedrawProgress'

import moment from 'moment'
import Vue from 'vue'
import VueC3 from 'vue-c3'

export default {
	name: 'PainNoteReport',

	components: {
		WebviewRedrawProgress,
		SorryPage,
		VueC3
	},

	data () {
		return {
			isLoading: false,
			isError: false,
			currentMonth: 0,
			monthBarDate: null,
			isMonthDisabled: false,
			month: null,
			painLevel: new Vue(),
			painHour: new Vue(),
			drugLevel: new Vue(),
			drugHour: new Vue(),
			barometricPressure: new Vue(),
			weather: new Vue(),
			barometricPressureDataCheck: true,
			weatherDataCheck: true,
			petType: 1, // 1: 人, 2: 犬, 3: 猫, 4: うさぎ, 5: 鳥, 6: ハムスター, 7: 魚, 8: カメ, 9: その他
			petClass: ''
		}
	},

	mounted () {
		// premiumTypeが10（ファミリーケアプラス）の場合はPetVoice表示・ペットアイコンの判定
		if (this.premiumType.premium_type_code === 10) {
			this.petType = this.userData.type
			this.petClass = this.petType !== 1 ? '-pet' : ''
		}
		this.exchangeMonth(0)
	},

	computed: {
		// map Vuex getters
		...mapGetters('common', ['userId', 'userToken', 'userData', 'premiumType']),
		...mapGetters('painNote', ['reportData']),

		// 日付表示
		computedMonthBarDate () {
			return this.monthBarDate
		},

		// 痛み回数表示
		computedPainCount () {
			let date = 0
			if (this.reportData !== null && this.reportData.records) {
				date = this.reportData.records.pain.total
			}
			return date
		},

		// クスリ回数表示
		computedDrugCount () {
			let date = 0
			if (this.reportData !== null && this.reportData.records) {
				date = this.reportData.records.drug.total
			}
			return date
		}
	},

	methods: {
		// map Vuex actions
		...mapActions('painNote', [SET_REPORT_DATA]),

		/**
		 * 日付表示処理&API呼び出し
		 */
		exchangeMonth (e) {
			this.currentMonth += e
			this.monthBarDate = moment(new Date()).add(this.currentMonth, 'month').format('YYYY年MM月')
			this.month = moment(new Date()).add(this.currentMonth, 'month').unix()
			this.isMonthDisabled = moment(new Date()).format('YYYY年MM月') === this.monthBarDate
			this.isLoading = true
			this.isError = false
			this.SET_REPORT_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				month: this.month
			}).then(res => {
				console.log('reportData: ', this.reportData)
				if (this.reportData.result_code !== 1001) {
					this.isError = true
				} else {
					// 成功時の処理
					this.painLevelGraph()
					this.painHourGraph()
					if (this.petType === 1) {
						this.drugLevelGraph()
						this.drugHourGraph()
					}
					this.barometricPressurePie()
					this.weatherPie()
				}
			}).catch(error => {
				// NOTE: エラーの処理
				console.log(error)
				this.isError = true
			}).finally(() => {
				this.isLoading = false
			})
		},

		/**
		 * リロード処理
		 */
		reload () {
			this.exchangeMonth(0)
		},

		/**
		* 痛みレベルのグラフ表示
		*/
		painLevelGraph () {
			// 痛みレベル用
			const maxPainNumber = Math.max.apply(null, [this.reportData.records.pain.by_level[2], this.reportData.records.pain.by_level[3], this.reportData.records.pain.by_level[4]])
			const pain1 = maxPainNumber === 0 ? 100 : maxPainNumber - this.reportData.records.pain.by_level[2]
			const pain2 = maxPainNumber === 0 ? 100 : maxPainNumber - this.reportData.records.pain.by_level[3]
			const pain3 = maxPainNumber === 0 ? 100 : maxPainNumber - this.reportData.records.pain.by_level[4]

			// 横棒グラフ
			const painLevel = {
				zerobased: false,
				size: {
					height: 152
				},
				bar: {
					width: 20,
					ratio: 1
				},
				data: {
					columns: [
						['existsData', this.reportData.records.pain.by_level[2], this.reportData.records.pain.by_level[3], this.reportData.records.pain.by_level[4]],
						['totalData', pain1, pain2, pain3]
					],
					type: 'bar',
					order: null,
					groups: [
						['existsData', 'totalData']
					],
					colors: {
						existsData: '#0071DB',
						totalData: '#E6E6E6'
					},
					onselected: false
				},
				axis: {
					rotated: true,
					x: {
						show: false
					},
					y: {
						max: maxPainNumber === 0 ? 100 : maxPainNumber,
						show: false,
						padding: {
							top: 14
						}
					}
				},
				legend: { show: false },
				tooltip: { show: false }
			}
			this.painLevel.$emit('init', painLevel)
		},

		/**
		* 痛み時間のグラフ表示
		*/
		painHourGraph () {
			// CHANGE: 1時～24時のグラフを表示していたが1時間ずれるバグがあった。実データとしては0時～23時表記で返しているようなので、0～23時にクライアントが合わせるよう修正。
			// 痛み記録時間用
			const data1Hour = ['existsData']
			const data2Hour = ['totalData']
			const maxDrugNumberArray = []
			Object.keys(this.reportData.records.pain.by_hour).map(item => {
				data1Hour.push(this.reportData.records.pain.by_hour[item])
				maxDrugNumberArray.push(this.reportData.records.pain.by_hour[item])
			})
			const maxDrugNumber = Math.max.apply(null, maxDrugNumberArray) === 0 ? 100 : Math.max.apply(null, maxDrugNumberArray)
			Object.keys(this.reportData.records.pain.by_hour).map(item => {
				data2Hour.push(maxDrugNumber - this.reportData.records.pain.by_hour[item])
			})
			// NOTE: ラベル[h]の縦軸を確保するために実態のないレコード（0）をpushする
			data1Hour.push(0)
			data2Hour.push(0)

			// 縦棒グラフ
			const painHour = {
				zerobased: false,
				size: {
					height: 180
				},
				bar: {
					width: 7
				},
				data: {
					x: 'x',
					columns: [
						['x', 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24],	// CHANGE: 文字列を指定しても数値にパースしているようなので、公式ドキュメントに合わせて数値に変更
						data2Hour,
						data1Hour
					],
					groups: [
						['existsData', 'totalData']
					],
					colors: {
						'existsData': '#0071DB',
						'totalData': '#E6E6E6'
					},
					type: 'bar',
					order: data2Hour
				},
				axis: {
					x: {
						padding: {
							right: 2
						},
						tick: {
							format: d => {
								// console.log(d)
								if (d === 24) {	// NOTE: 値が数字じゃないとパースエラーが発生するため、24を[h]に置き換えるようにしている（理想はdata.columnsの24を[h]としておきたい）
									return '[h]'
								} else {
									return d
								}
							},
							outer: false
						}
					},
					y: {
						max: maxDrugNumber,
						min: maxDrugNumber === 100 ? 10 : 0.5,
						show: false
					}
				},
				legend: { show: false },
				tooltip: { show: false }
			}
			this.painHour.$emit('init', painHour)
		},

		/**
		* クスリレベルのグラフ表示
		*/
		drugLevelGraph () {
			const maxPainNumber = Math.max.apply(null, [this.reportData.records.drug.by_level[2], this.reportData.records.drug.by_level[3]])
			const drug1 = maxPainNumber === 0 ? 100 : maxPainNumber - this.reportData.records.drug.by_level[2]
			const drug2 = maxPainNumber === 0 ? 100 : maxPainNumber - this.reportData.records.drug.by_level[3]

			// 横棒グラフ
			const drugLevel = {
				zerobased: false,
				size: {
					height: 104
				},
				bar: {
					width: 20,
					ratio: 1
				},
				data: {
					columns: [
						['existsData', this.reportData.records.drug.by_level[2], this.reportData.records.drug.by_level[3]],
						['totalData', drug1, drug2]
					],
					type: 'bar',
					order: null,
					groups: [
						['existsData', 'totalData']
					],
					colors: {
						existsData: '#0071DB',
						totalData: '#E6E6E6'
					},
					onselected: false
				},
				axis: {
					rotated: true,
					x: {
						show: false
					},
					y: {
						max: maxPainNumber === 0 ? 100 : maxPainNumber,
						show: false,
						padding: {
							top: 14
						}
					}
				},
				legend: { show: false },
				tooltip: { show: false }
			}
			this.drugLevel.$emit('init', drugLevel)
		},

		/**
		* クスリ時間のグラフ表示
		*/
		drugHourGraph () {
			// CHANGE: 1時～24時のグラフを表示していたが1時間ずれるバグがあった。実データとしては0時～23時表記で返しているようなので、0～23時にクライアントが合わせるよう修正。
			const data1Hour = ['existsData']
			const data2Hour = ['totalData']
			const maxDrugNumberArray = []
			Object.keys(this.reportData.records.drug.by_hour).map((item) => {
				data1Hour.push(this.reportData.records.drug.by_hour[item])
				maxDrugNumberArray.push(this.reportData.records.drug.by_hour[item])
			})
			const maxDrugNumber = Math.max.apply(null, maxDrugNumberArray) === 0 ? 100 : Math.max.apply(null, maxDrugNumberArray)
			Object.keys(this.reportData.records.drug.by_hour).map((item) => {
				data2Hour.push(maxDrugNumber - this.reportData.records.drug.by_hour[item])
			})
			// NOTE: ラベル[h]の縦軸を確保するために実態のないレコード（0）をpushする
			data1Hour.push(0)
			data2Hour.push(0)

			// 縦棒グラフ
			const drugHour = {
				zerobased: false,
				size: {
					height: 180
				},
				bar: {
					width: 7
				},
				data: {
					x: 'x',
					columns: [
						['x', 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24],	// CHANGE: 文字列を指定しても数値にパースしているようなので、公式ドキュメントに合わせて数値に変更
						data2Hour,
						data1Hour
					],
					groups: [
						['existsData', 'totalData']
					],
					colors: {
						'existsData': '#0071DB',
						'totalData': '#E6E6E6'
					},
					type: 'bar'
				},
				axis: {
					x: {
						padding: {
							right: 2
						},
						tick: {
							format: d => {
								if (d === 24) {	// NOTE: 値が数字じゃないとパースエラーが発生するため、24を[h]に置き換えるようにしている（理想はdata.columnsの24を[h]としておきたい）
									return '[h]'
								} else {
									return d
								}
							},
							outer: false
						}
					},
					y: {
						max: maxDrugNumber,
						min: maxDrugNumber === 100 ? 10 : 0.5,
						show: false
					}
				},
				legend: { show: false },
				tooltip: { show: false }
			}
			this.drugHour.$emit('init', drugHour)
		},
		/**
		* 痛み発生時の気圧の表示
		*/
		barometricPressurePie () {
			this.barometricPressureDataCheck = this.reportData.records.pressure.pain_by_desc !== 0 || this.reportData.records.pressure.pain_by_asc !== 0

			// 円グラフ
			const barometricPressure = {
				data: {
					columns: [
						['barometricPressureRise', this.reportData.records.pressure.pain_by_asc],
						['barometricPressureDrop', this.reportData.records.pressure.pain_by_desc],
						['noData', this.barometricPressureDataCheck ? 0 : 100]
					],
					type: 'pie',
					order: null,
					colors: {
						'barometricPressureRise': '#6EA5DB',
						'barometricPressureDrop': '#0071DB',
						'noData': '#E6E6E6'
					}
				},
				pie: {
					expand: false,
					label: {
						show: false
					}
				},
				legend: { show: false },
				tooltip: { show: false }
			}
			this.barometricPressure.$emit('init', barometricPressure)
		},
		/**
		* 痛み発生時の天気の表示
		*/
		weatherPie () {
			this.weatherDataCheck = this.reportData.records.weather.pain_by_sunny !== 0 || this.reportData.records.weather.pain_by_cloudy !== 0 || this.reportData.records.weather.pain_by_rain_snow !== 0
			// 円グラフ
			const weather = {
				data: {
					columns: [
						['snow', this.reportData.records.weather.pain_by_rain_snow],
						['cloudy', this.reportData.records.weather.pain_by_cloudy],
						['sunny', this.reportData.records.weather.pain_by_sunny],
						['noData', this.weatherDataCheck ? 0 : 100]
					],
					type: 'pie',
					order: null,
					colors: {
						'snow': '#0071DB',
						'cloudy': '#6EA5DB',
						'sunny': '#B0C6DB',
						'noData': '#E6E6E6'
					}
				},
				pie: {
					expand: false,
					label: {
						show: false
					}
				},
				legend: { show: false },
				tooltip: { show: false }
			}
			this.weather.$emit('init', weather)
		}
	}
}
</script>

<style lang="scss" scoped>
@import "../assets/sass/variable";

.pain-note-report {
	background: $background-secondary;
	width: 100%;
	height: 100%;
	&-monthbar {
		width: 100%;
		height: auto;
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-family: $family-primary;
		background: $_white;
		padding: $spacing-10 0;
		position: absolute;
		top: 0;
		transform: translate3d(0, 0, 0);
		z-index: 1;
		&__icon {
			color: $calendar-month-primary-icon;
			font-size: $font-size-24;
			padding: 0 $spacing-20;
			&.is-disabled {
				color: $calendar-month-secondary-icon;
				pointer-events: none;
			}
		}
	}
	&__content {
		width: 100%;
		height: 100%;
		overflow-y: scroll;
	}
	&-pain {
		width: auto;
		padding: 56px $spacing-20 $spacing-20 $spacing-20;
		border-bottom: 1px solid $report-border-bottom;
		display: block;
		h2 {
			margin: $spacing-20 0 0 0;
			font-size: $font-size-20;
			font-family: $family-primary;
		}
		p {
			text-align: center;
			font-size: $font-size-44;
			font-family: $family-secondary;
			margin: 0;
			color: $report-title-color;
		}
		&__area {
			width: 100%;
			display: flex;
			&--left {
				width: 20%;
				margin-top: $spacing-6;
				i {
					margin: 0 auto;
				}
			}
			&--right {
				width: 80%;
				margin: 0 auto;
			}
		}
		&__hour {
			width: 100%;
			clear: both;
		}
	}
	&-drug {
		width: auto;
		padding: $spacing-20 $spacing-20;
		border-bottom: 1px solid $report-border-bottom;
		display: block;
		h2 {
			margin: 0;
			font-size: $font-size-20;
			font-family: $family-primary;
		}
		p {
			text-align: center;
			font-size: $font-size-44;
			font-family: $family-secondary;
			margin: 0;
			color: $report-title-color;
		}
		&__area {
			width: 100%;
			display: flex;
			&--left {
				width: 20%;
				margin-top: $spacing-6;
				i {
					margin: 0 auto;
				}
			}
			&--right {
				width: 80%;
				margin: 0 auto;
			}
		}
		&__hour {
			width: 100%;
			clear: both;
		}
	}
	&-barometricpressure {
		width: auto;
		padding: $spacing-20 $spacing-20;
		border-bottom: 1px solid $report-border-bottom;
		margin: 0 auto;
		h2 {
			margin: 0;
			font-size: $font-size-20;
			font-family: $family-primary;
		}
		&__area {
			width: 100%;
			margin-top: $spacing-10;
			position: relative;
			&--pie {
				width: 100%;
				height: auto;
				max-height: 300px !important;
			}
			&--text {
				width: 100%;
				height: 300px;
				position: absolute;
				top: 0;
				margin: 0 auto;
				display: flex;
				align-items: center;
				justify-content: center;
				p {
					margin: 0;
					font-size: $font-size-12;
					font-family: $family-secondary;
				}
			}
		}
		&__label {
			width: 100%;
			height: 50px;
			position: relative;
			text-align: center;
			&--left {
				width: 80px;
				height: 50px;
				position: absolute;
				left: 0;
				p {
					padding-top: $spacing-10;
					font-size: $font-size-14;
					font-family: $family-primary;
				}
				p::before {
					content: "";
					width: 20px;
					height: 20px;
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					margin: 0 auto;
					background-color: $report-left-box-color;
				}
			}
			&--right {
				width: 80px;
				height: 50px;
				position: absolute;
				right: 0;
				p {
					padding-top: $spacing-10;
					font-size: $font-size-14;
					font-family: $family-primary;
				}
				p::before {
					content: "";
					width: 20px;
					height: 20px;
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					margin: 0 auto;
					background-color: $report-right-box-color;
				}
			}
		}
	}
	&-weather {
		width: auto;
		padding: $spacing-20 $spacing-20;
		margin: 0 auto;
		h2 {
			margin: 0;
			font-size: $font-size-20;
			font-family: $family-primary;
		}
		&__area {
			width: 100%;
			margin-top: $spacing-10;
			position: relative;
			&--pie {
				width: 100%;
				height: auto;
				max-height: 300px !important;
			}
			&--text {
				width: 100%;
				height: 300px;
				position: absolute;
				top: 0;
				margin: 0 auto;
				display: flex;
				align-items: center;
				justify-content: center;
				p {
					margin: 0;
					font-size: $font-size-12;
					font-family: $family-secondary;
				}
			}
		}
		&__label {
			width: 100%;
			height: 50px;
			position: relative;
			text-align: center;
			&--left {
				width: 80px;
				position: absolute;
				left: 0;
				p {
					padding-top: $spacing-10;
					font-size: $font-size-14;
					font-family: $family-primary;
				}
				p::before {
					content: "";
					width: 20px;
					height: 20px;
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					margin: 0 auto;
					background-color: $report-sunny-box-color;
				}
			}
			&--center {
				width: 80px;
				position: absolute;
				left: 0;
				right: 0;
				margin: 0 auto;
				p {
					padding-top: $spacing-10;
					font-size: $font-size-14;
					font-family: $family-primary;
				}
				p::before {
					content: "";
					width: 20px;
					height: 20px;
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					margin: 0 auto;
					background-color: $report-cloudy-box-color;
				}
			}
			&--right {
				width: 80px;
				position: absolute;
				right: 0;
				p {
					padding-top: $spacing-10;
					font-size: $font-size-14;
					font-family: $family-primary;
				}
				p::before {
					content: "";
					width: 20px;
					height: 20px;
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					margin: 0 auto;
					background-color: $report-snow-box-color;
				}
			}
		}
	}

	/deep/.c3-axis-x {
		font-size: $font-size-12;
		font-family: $family-secondary;
	}
}

// TODO: 各スプライトの位置をハードコーディングしているため、スプライト画像を変更する際は位置を再設定する必要がある、もしくは正しくスプライト画像の位置を設定しCSSで調整する必要がある
// スプライト画像。
.pain-report_sprite {
	// background-image: url(~@/assets/img/pain_sprite_icon.png);
	background-image: url(~@/assets/img/sprite_pain_icon.png);	// こちらのスプライト画像を正とする
	background-repeat: no-repeat;
	display: block;
}

.pain-report_sprite.sprite-inp_icon_drug_otc_3 {
	width: 82px;
	height: 82px;
	background-position: -8px -8px;
	transform:scale(1);
	zoom: 0.6;
}

.pain-report_sprite.sprite-inp_icon_drug_pre_3 {
	width: 82px;
	height: 82px;
	background-position: -8px -105px;
	transform:scale(1);
	zoom: 0.6;
}

.pain-report_sprite.sprite-inp_icon_pain2_4 {
	width: 82px;
	height: 82px;
	background-position: -8px -305px;
	transform:scale(1);
	zoom: 0.6;
}

.pain-report_sprite.sprite-inp_icon_pain3_4 {
	width: 82px;
	height: 82px;
	background-position: -8px -405px;
	transform:scale(1);
	zoom: 0.6;
}

.pain-report_sprite.sprite-inp_icon_pain4_4 {
	width: 82px;
	height: 82px;
	background-position: -8px -505px;
	transform:scale(1);
	zoom: 0.6;
}

// ペットアイコンのスプライト
.pain-report_sprite-pet {
	background-image: url(~@/assets/img/pet/sprite_pet_pain.png);
	background-repeat: no-repeat;
	display: block;

	&.sprite-inp_icon_pain2_4 {
		width: 82px;
		height: 82px;
		background-position: -13px -122px;
		zoom: 0.6;
	}

	&.sprite-inp_icon_pain3_4 {
		width: 82px;
		height: 82px;
		background-position: -13px -231px;
		zoom: 0.6;
	}

	&.sprite-inp_icon_pain4_4 {
		width: 82px;
		height: 82px;
		background-position: -13px -340px;
		zoom: 0.6;
	}
}
</style>
